/* eslint-disable react/jsx-pascal-case */
import { useContext, useEffect, useState, useMemo } from "react";
import { UserContext } from "../../Context/UserContext";
import OpportunityService from "../../api/opportunityService";
import Req_Rec_Card from "./Req_Rec_Card";
import { CircularProgress } from "@material-ui/core";
import CustomDateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import { FaCalendarAlt, FaChevronRight, FaChevronDown } from "react-icons/fa";
import { DropDownMUI } from "../../components";

// Helper function to check if a date lies within a range
const isDateLiesCheck = (date, range) => {
  if (!date || !range || !range[0] || !range[1]) return false;
  
  const checkDate = new Date(date);
  const startDate = new Date(range[0]);
  const endDate = new Date(range[1]);
  
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);
  
  return checkDate >= startDate && checkDate <= endDate;
};

// Helper function to check if item is a first request
const isFirstRequestItem = (item) => {
  try {
    const parsedHistory = JSON.parse(item.History || "{}");
    const firstHistoryEntry = Object.values(parsedHistory)?.[0];
    return firstHistoryEntry?.firstRequest === true;
  } catch {
    return false;
  }
};

const TimeBubbles = ({ viewMode, setViewMode }) => {
  // Define color palette for a professional look
  const colors = {
    primary: "#fdd34d",
    hover: "#003d7a",
    inactive: "#f8f9fa",
    border: "#dee2e6",
    text: "#212529",
    textLight: "#6c757d",
    textActive: "#353535",
  };

  // Container styles
  const containerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.5rem",
    borderRadius: "12px",
    backgroundColor: "#ffffff",
    width: "fit-content",
  };

  // Bubble base styles
  const bubbleBaseStyle = {
    padding: "0.625rem 1.25rem",
    margin: "0 0.25rem",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontFamily:
      "SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, sans-serif",
    fontWeight: "500",
    fontSize: "0.875rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "5rem",
  };

  // Active bubble styles
  const activeBubbleStyle = {
    ...bubbleBaseStyle,
    backgroundColor: colors.primary,
    color: colors.textActive,
    boxShadow: "0 2px 8px rgba(0, 86, 179, 0.25)",
  };

  // Inactive bubble styles
  const inactiveBubbleStyle = {
    ...bubbleBaseStyle,
    backgroundColor: colors.inactive,
    color: colors.textLight,
    border: `1px solid ${colors.border}`,
  };

  // Demo component with state
  const [internalViewMode, setInternalViewMode] = useState(viewMode || "daily");

  const handleSetViewMode = (mode) => {
    if (setViewMode) {
      setViewMode(mode);
    } else {
      setInternalViewMode(mode);
    }
  };

  const currentViewMode = viewMode || internalViewMode;

  return (
    <div style={containerStyle}>
      <div
        style={
          currentViewMode === "daily" ? activeBubbleStyle : inactiveBubbleStyle
        }
        onClick={() => handleSetViewMode("daily")}
        onMouseOver={(e) => {
          if (currentViewMode !== "daily") {
            e.currentTarget.style.backgroundColor = "#f1f3f5";
          }
        }}
        onMouseOut={(e) => {
          if (currentViewMode !== "daily") {
            e.currentTarget.style.backgroundColor = colors.inactive;
          }
        }}
      >
        Daily
      </div>
      <div
        style={
          currentViewMode === "weekly" ? activeBubbleStyle : inactiveBubbleStyle
        }
        onClick={() => handleSetViewMode("weekly")}
        onMouseOver={(e) => {
          if (currentViewMode !== "weekly") {
            e.currentTarget.style.backgroundColor = "#f1f3f5";
          }
        }}
        onMouseOut={(e) => {
          if (currentViewMode !== "weekly") {
            e.currentTarget.style.backgroundColor = colors.inactive;
          }
        }}
      >
        Weekly
      </div>
      <div
        style={
          currentViewMode === "monthly"
            ? activeBubbleStyle
            : inactiveBubbleStyle
        }
        onClick={() => handleSetViewMode("monthly")}
        onMouseOver={(e) => {
          if (currentViewMode !== "monthly") {
            e.currentTarget.style.backgroundColor = "#f1f3f5";
          }
        }}
        onMouseOut={(e) => {
          if (currentViewMode !== "monthly") {
            e.currentTarget.style.backgroundColor = colors.inactive;
          }
        }}
      >
        Monthly
      </div>
    </div>
  );
};

const TimeGroupHeader = ({
  dateRange,
  isOpen,
  toggleOpen,
  summary = {
    total: 0,
    approved: 0,
    pending: 0,
    declined: 0,
  },
  projects = [],
  projectGroups,
  firstRequest,
}) => {
  const formatDateRange = (range) => {
    const [start, end] = range;
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    const options = { 
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    };
    
    return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
  };

  return (
    <div className="time-group-header" onClick={toggleOpen}>
      <div className="time-group-title">
        <div className="date-range">
          <FaCalendarAlt className="calendar-icon" />
          <span>{formatDateRange(dateRange)}</span>
          <div className="toggle-indicator">
            {isOpen ? <FaChevronDown /> : <FaChevronRight />}
          </div>
        </div>
        
        <div className="amount-summary">
          <div className="amount-badge total">
            <span>Total</span>
            <span>₹{summary.total.toLocaleString('en-IN')}</span>
          </div>
        </div>
      </div>
      
      <div className="time-period-stats">
        <div className="stat-item requests">
          <div className="stat-dot"></div>
          <span>{projects.length} Projects</span>
        </div>
      </div>
      
      <div className="project-list">
        {projects.map((project, idx) => (
          <div key={idx} className="project-tag">
            <div className="project-dot"></div>
            <span>{project}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProjectGroup = ({
  projectName,
  projectRecords,
  isOpen,
  toggleOpen,
  setFinRecordsData,
  setRawData,
  summary = {
    total: 0,
    approved: 0,
    pending: 0,
    approvedCount: 0,
    pendingCount: 0,
  },
}) => {
  return (
    <div className="project-group">
      <div className="project-group-header" onClick={toggleOpen}>
        <div className="project-title">
          <span>{projectName}</span>
          <div className={`toggle-icon ${isOpen ? 'expanded' : ''}`}>
            {isOpen ? <FaChevronDown /> : <FaChevronRight />}
          </div>
        </div>
        
        <div className="project-amount-summary">
          <div className="project-amount-badge total">
            <span>Total</span>
            <span>₹{summary.total.toLocaleString('en-IN')}</span>
          </div>
        </div>
      </div>
      
      <div className={`project-requests ${isOpen ? 'expanded' : ''}`}>
        {projectRecords.map((record, index) => {
          const isFirstRequest = isFirstRequestItem(record);
          return (
            <div
              key={index}
              style={{
                position: "relative",
                ...(isFirstRequest && {
                  border: "2px solid #fbbf24",
                  borderRadius: "12px",
                  backgroundColor: "#fffbeb",
                  padding: "8px",
                  margin: "4px 0",
                  boxShadow: "0 2px 8px rgba(251, 191, 36, 0.15)",
                })
              }}
            >
              {isFirstRequest && (
                <div
                  style={{
                    position: "absolute",
                    top: "-8px",
                    right: "8px",
                    backgroundColor: "#fbbf24",
                    color: "#78350f",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "10px",
                    fontWeight: "600",
                    zIndex: 1,
                  }}
                >
                  FIRST REQUEST
                </div>
              )}
              <Req_Rec_Card
                rIndex={index}
                setRawData={setRawData}
                request={record}
                isRecord={true}
                setFinRequestsData={setFinRecordsData}
                className="nested-card"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const getDateRanges = (viewMode, startDate = new Date()) => {
  const ranges = [];
  const currentDate = new Date(startDate);
  
  switch (viewMode) {
    case 'daily':
      // Get last 7 days
      for (let i = 0; i < 7; i++) {
        const start = new Date(currentDate);
        start.setDate(start.getDate() - i);
        start.setHours(0, 0, 0, 0);
        
        const end = new Date(start);
        end.setHours(23, 59, 59, 999);
        
        ranges.push([start, end]);
      }
      break;
      
    case 'weekly':
      // Get last 4 weeks
      for (let i = 0; i < 4; i++) {
        const end = new Date(currentDate);
        end.setDate(end.getDate() - (i * 7));
        end.setHours(23, 59, 59, 999);
        
        const start = new Date(end);
        start.setDate(start.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        
        ranges.push([start, end]);
      }
      break;
      
    case 'monthly':
      // Get last 3 months
      for (let i = 0; i < 3; i++) {
        const start = new Date(currentDate);
        start.setMonth(start.getMonth() - i);
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        
        const end = new Date(start);
        end.setMonth(end.getMonth() + 1);
        end.setDate(0);
        end.setHours(23, 59, 59, 999);
        
        ranges.push([start, end]);
      }
      break;
  }
  
  return ranges;
};

const isDateInRange = (date, range) => {
  const [start, end] = range;
  const checkDate = new Date(date);
  return checkDate >= start && checkDate <= end;
};

const TimeBasedView = ({
  finRecordsData,
  setFinRecordsData,
  setRawData,
  viewMode,
}) => {
  const [openTimeGroups, setOpenTimeGroups] = useState({});
  const [openProjectGroups, setOpenProjectGroups] = useState({});

  const dateRanges = getDateRanges(viewMode);
  
  const groupRecordsByDateRange = (records, ranges) => {
    return ranges.reduce((groups, range) => {
      const recordsInRange = records.filter(record => 
        isDateInRange(record.createdAt, range)
      );
      if (recordsInRange.length > 0) {
        groups.push({
          dateRange: range,
          records: recordsInRange,
          firstRequest: recordsInRange[0]
        });
      }
      return groups;
    }, []);
  };

  const calculateSummary = (records) => {
    return records.reduce((summary, record) => {
      const amount = parseFloat(record.requestItemData?.amount || 0);
      summary.total += amount;
      return summary;
    }, { total: 0, approved: 0, pending: 0, declined: 0 });
  };

  const toggleTimeGroup = (rangeKey) => {
    setOpenTimeGroups(prev => ({
      ...prev,
      [rangeKey]: !prev[rangeKey]
    }));
  };

  const toggleProjectGroup = (rangeKey, projectKey) => {
    const groupKey = `${rangeKey}-${projectKey}`;
    setOpenProjectGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const timeGroups = groupRecordsByDateRange(finRecordsData, dateRanges);
  
  return (
    <div className="time-based-view">
      {timeGroups.map((group, idx) => {
        const { dateRange, records, firstRequest } = group;
        const projectGroups = groupRecordsByProject(records);
        const summary = calculateSummary(records);
        const projects = Object.keys(projectGroups);
        const rangeKey = dateRange[0].toISOString();
        
        return (
          <div key={rangeKey} className="time-group">
            <TimeGroupHeader
              dateRange={dateRange}
              isOpen={openTimeGroups[rangeKey]}
              toggleOpen={() => toggleTimeGroup(rangeKey)}
              summary={summary}
              projects={projects}
              projectGroups={projectGroups}
              firstRequest={firstRequest}
            />
            
            {openTimeGroups[rangeKey] && (
              <div className="project-groups">
                {Object.entries(projectGroups).map(([projectName, projectRecords]) => {
                  const groupKey = `${rangeKey}-${projectName}`;
                  const projectSummary = calculateSummary(projectRecords);
                  
                  return (
                    <ProjectGroup
                      key={groupKey}
                      projectName={projectName}
                      projectRecords={projectRecords}
                      isOpen={openProjectGroups[groupKey]}
                      toggleOpen={() => toggleProjectGroup(rangeKey, projectName)}
                      setFinRecordsData={setFinRecordsData}
                      setRawData={setRawData}
                      summary={projectSummary}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const filterRecordsData = (
  resultsArr,
  statusFilter,
  projectFilter,
  DateRangeValue,
  categoryFilter
) => {
  if (!Array.isArray(resultsArr)) {
    console.log("filterRecordsData: resultsArr is not an array");
    return [];
  }

  console.log("filterRecordsData input:", { 
    resultsArrLength: resultsArr.length, 
    statusFilter, 
    projectFilter, 
    DateRangeValue, 
    categoryFilter 
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr);
    } catch (e) {
      console.error("Invalid date format:", dateStr);
      return null;
    }
  };

  // First item for debugging
  if (resultsArr.length > 0) {
    console.log("Sample record structure:", JSON.stringify(resultsArr[0], null, 2));
  }

  return resultsArr.filter((item) => {
    // Debugging item structure
    if (!item) {
      console.log("Found null/undefined item in resultsArr");
      return false;
    }

    // Handle potential undefined values
    const status = item?.requestItemData?.status || item?.Status || '';
    const projectName = item?.requestItemData?.projectName || item?.ProjectDetails || '';
    const category = item?.requestItemData?.category || item?.Category || '';
    const date = formatDate(item?.createdAt || item?.DOFReq || '');

    const statusMatch = statusFilter === "All" || status === statusFilter;
    const projectMatch = projectFilter === "All" || 
                         (typeof projectName === 'string' && projectName.includes(projectFilter));
    const categoryMatch = categoryFilter === "All" || 
                         (typeof category === 'string' && category.includes(categoryFilter));
    
    // Date matching logic
    let dateMatch = true;
    if (DateRangeValue[0] && DateRangeValue[1] && date) {
      dateMatch = isDateLiesCheck(date, DateRangeValue);
    }

    // Log item info if filtering seems to be excluding everything
    if (!statusMatch || !projectMatch || !categoryMatch || !dateMatch) {
      console.log("Item filtered out:", { 
        status, 
        projectName, 
        category, 
        date,
        statusMatch, 
        projectMatch, 
        categoryMatch, 
        dateMatch 
      });
    }

    return statusMatch && projectMatch && categoryMatch && dateMatch;
  });
};

// Use a completely different approach to grouping projects that ensures records are preserved
const groupRecordsByProject = (records) => {
  if (!Array.isArray(records) || records.length === 0) {
    console.warn("groupRecordsByProject: empty or non-array input");
    return {};
  }
  
  // Direct approach - no complex transformations
  const projectGroups = {};
  
  // Group records by project name
  records.forEach(record => {
    if (!record) return;
    
    // Get project name
    let projectName = 'Unassigned';
    try {
      if (record.requestItemData && record.requestItemData.projectName) {
        projectName = record.requestItemData.projectName;
      } else if (record.ProjectDetails) {
        if (typeof record.ProjectDetails === 'string') {
          if (record.ProjectDetails.includes('{')) {
            const projectDetails = JSON.parse(record.ProjectDetails);
            projectName = Object.values(projectDetails)[0] || 'Unassigned';
          } else {
            projectName = record.ProjectDetails;
          }
        }
      }
    } catch (e) {
      console.error("Error extracting project name:", e);
    }
    
    // Initialize project group if needed
    if (!projectGroups[projectName]) {
      projectGroups[projectName] = [];
    }
    
    // Add record to project group
    projectGroups[projectName].push(record);
  });
  
  // Transform into the expected format with calculated totals
  const result = {};
  
  Object.entries(projectGroups).forEach(([projectName, projectRecords]) => {
    // Calculate project total
    let total = 0;
    let firstRequest = null;
    
    projectRecords.forEach(record => {
      // Add to total
      let amount = 0;
      if (record.requestItemData && record.requestItemData.amount) {
        amount = parseFloat(record.requestItemData.amount || 0);
      } else if (record.Amount) {
        amount = parseFloat(record.Amount || 0);
      } else if (record.AmountRequested) {
        amount = parseFloat(record.AmountRequested || 0);
      }
      total += amount;
      
      // Check for first request
      try {
        if (!firstRequest && record.History) {
          const history = JSON.parse(record.History);
          const firstEntry = Object.values(history)[0];
          if (firstEntry && firstEntry.firstRequest === true) {
            firstRequest = record;
          }
        }
      } catch (e) {}
    });
    
    // Store the project data
    result[projectName] = {
      // Store the raw records array directly
      records: projectRecords,
      total: total,
      firstRequest: firstRequest
    };
  });
  
  return result;
};

// Add debug logging
const debugRecord = (record) => {
  try {
    if (!record) {
      console.error("Debug: Record is null or undefined");
      return "Invalid record";
    }
    
    // Check basic properties
    const hasUuid = Boolean(record.uuid);
    const hasOrderId = Boolean(record.OrderId);
    const hasHistory = Boolean(record.History);
    const hasProjectDetails = Boolean(record.ProjectDetails);
    const hasAmount = Boolean(record.AmountRequested || 
                             (record.requestItemData && record.requestItemData.amount));
    
    console.log("Record debug info:", {
      hasUuid,
      hasOrderId,
      hasHistory,
      hasProjectDetails,
      hasAmount,
      uuid: record.uuid?.substring(0, 8) + '...',
      type: typeof record
    });
    
    return "Record looks OK";
  } catch (e) {
    console.error("Error debugging record:", e);
    return "Error debugging";
  }
};

const Records_Fin = () => {
  const userContext = useContext(UserContext);
  const [rawData, setRawData] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [finRecordsData, setFinRecordsData] = useState([]);
  const [DateRangeValue, setDateRangeValue] = useState([null, null]);
  const [viewMode, setViewMode] = useState("daily");
  const [statusFilter, setStatusFilter] = useState("All");
  const [projectFilter, setProjectFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [projectNames, setProjectNames] = useState([]);
  const [categoryNames, setCategoryNames] = useState([]);
  const [expandAll, setExpandAll] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [expandedProjects, setExpandedProjects] = useState({});

  // Modify toggleProject to log expanded projects and force a re-render
  const toggleProject = (groupKey) => {
    
    setExpandedProjects(prev => {
      // If this project was not expanded before, log some debug info about its records
      if (!prev[groupKey]) {
        const timeGroups = groupRecordsByTimeRange();
        const [timeGroupId, projectName] = groupKey.split('-');
        const timeGroup = timeGroups[timeGroupId];
        
        if (timeGroup) {
          const projectGroups = groupRecordsByProject(timeGroup.records);
          const projectData = projectGroups[projectName];
          
          if (projectData && projectData.records) {
            console.log(`Project ${projectName} has ${projectData.records.length} records:`);
            if (projectData.records.length > 0) {
              debugRecord(projectData.records[0]);
            }
          } else {
            console.log(`No records found for project ${projectName}`);
          }
        }
      }
      
      const newState = {
        ...prev,
        [groupKey]: !prev[groupKey]
      };
      
      console.log("Expanded projects:", Object.keys(newState).filter(k => newState[k]));
      return newState;
    });

  };

  const getFinanceRecords = async () => {
    setIsRefreshing(true);
    const opportunityService = new OpportunityService();
    try {
      
      const recordData = await opportunityService.getFinanceRecord(
        userContext.user.Firm
      );
      console.log("Finance records response:", recordData);
      
      if (recordData.status === 200 && Array.isArray(recordData.results)) {
        console.log("Setting raw data:", recordData.results);
        setRawData(recordData.results);
      } else {
        console.error("Invalid data format returned:", recordData);
        setRawData([]);
      }
    } catch (error) {
      console.error("Error fetching finance records:", error);
      setRawData([]);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    getFinanceRecords();
    
    // Cleanup function to prevent state updates if component unmounts during fetch
    return () => {
      setIsRefreshing(false);
      setLoading(false);
    };
  }, []);

  useEffect(() => {
    if (rawData && Array.isArray(rawData)) {
      try {
        console.log("Processing raw data:", rawData);
        const projectwiseData = filterRecordsData(
          rawData,
          statusFilter,
          projectFilter,
          DateRangeValue,
          categoryFilter
        );
        console.log("Filtered records data:", projectwiseData);
        setFinRecordsData(projectwiseData);

        // Extract project names with safer approach
        const projects = rawData
          .filter(item => item && item.requestItemData)
          .map(item => item.requestItemData?.projectName)
          .filter(name => name && name.trim().length > 0);
        
        setProjectNames([...new Set(projects)]);

        // Extract category names with safer approach
        const categories = rawData
          .filter(item => item && item.requestItemData)
          .map(item => item.requestItemData?.category)
          .filter(category => category && category.trim().length > 0);
        
        setCategoryNames([...new Set(categories)]);
      } catch (error) {
        console.error("Error processing records data:", error);
        // Set defaults in case of error
        setFinRecordsData([]);
        setProjectNames([]);
        setCategoryNames([]);
      }
    } else {
      // Set defaults when no valid data
      setFinRecordsData([]);
      setProjectNames([]);
      setCategoryNames([]);
    }
  }, [rawData, DateRangeValue, statusFilter, projectFilter, categoryFilter]);

  useEffect(() => {
    // Set first date group to be expanded by default
    if (finRecordsData && finRecordsData.length > 0) {
      const timeGroups = groupRecordsByTimeRange();
      if (Object.keys(timeGroups).length > 0) {
        const firstGroupId = Object.keys(timeGroups)[0];
        
        // Expand the first date group
        setOpenGroups(prev => ({
          ...prev,
          [firstGroupId]: true
        }));
        
        // Special debug: Find project groups for first time group
        try {
          // If we're in weekly or monthly view, also expand the first project in the first group
          if (viewMode !== 'daily') {
            const firstGroup = timeGroups[firstGroupId];
            if (firstGroup && firstGroup.records && firstGroup.records.length > 0) {
              const projectGroups = groupRecordsByProject(firstGroup.records);
              console.log("Debug - Found project groups:", Object.keys(projectGroups));
              
              // Expand the first project if available
              const projectNames = Object.keys(projectGroups);
              if (projectNames.length > 0) {
                const firstProject = projectNames[0];
                const groupKey = `${firstGroupId}-${firstProject}`;
                
                console.log(`Debug - Setting expanded project: ${groupKey}`);
                setExpandedProjects(prev => ({
                  ...prev,
                  [groupKey]: true
                }));
              }
            }
          }
        } catch (error) {
          console.error("Error expanding first project:", error);
        }
      }
    }
  }, [finRecordsData, viewMode]);

  const calculateTotalAmount = () => {
    if (!Array.isArray(finRecordsData)) return 0;
    
    return finRecordsData.reduce((total, item) => {
      // Use paid amount if available, otherwise use regular amount
      let amount = 0;
      if (item.AmountPaid) {
        amount = parseFloat(item.AmountPaid || 0);
      } else if (item.requestItemData && item.requestItemData.paidAmount) {
        amount = parseFloat(item.requestItemData.paidAmount || 0);
      } else if (item.requestItemData && item.requestItemData.amount) {
        amount = parseFloat(item.requestItemData.amount || 0);
      }
      return total + amount;
    }, 0);
  };

  const groupRecordsByTimeRange = () => {
    if (!Array.isArray(finRecordsData)) {
      console.log("groupRecordsByTimeRange: finRecordsData is not an array");
      return {};
    }
    
    console.log("Grouping records by time range, count:", finRecordsData.length);
    
    const groups = {};
    
    finRecordsData.forEach(record => {
      // Make sure record has a valid date
      const dateValue = record.createdAt || record.DOFReq;
      if (!dateValue) {
        console.log("Record missing date, skipping:", record);
        return;
      }
      
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        console.log("Invalid date value:", dateValue);
        return;
      }
      
      let key;
      
      switch(viewMode) {
        case 'monthly':
          key = `${date.getFullYear()}-${date.getMonth() + 1}`;
          break;
        case 'weekly':
          // Get the Monday of the week
          const monday = new Date(date);
          monday.setDate(date.getDate() - date.getDay() + 1);
          key = monday.toISOString().split('T')[0];
          break;
        default: // daily
          key = date.toISOString().split('T')[0];
      }
      
      if (!groups[key]) {
        groups[key] = {
          records: [],
          firstRequest: null,
          total: 0,
          projects: new Set()
        };
      }
      
      groups[key].records.push(record);
      
      // Add amount to total - handle different data structures
      let amount = 0;
      if (record.requestItemData && record.requestItemData.amount) {
        amount = parseFloat(record.requestItemData.amount || 0);
      } else if (record.Amount) {
        amount = parseFloat(record.Amount || 0);
      } else if (record.AmountRequested) {
        amount = parseFloat(record.AmountRequested || 0);
      }
      
      groups[key].total += amount;
      
      // Add project to set of projects - handle different data structures
      let projectName = '';
      if (record.requestItemData && record.requestItemData.projectName) {
        projectName = record.requestItemData.projectName;
      } else if (record.ProjectDetails) {
        try {
          // If ProjectDetails is a JSON string, parse it
          if (typeof record.ProjectDetails === 'string' && record.ProjectDetails.includes('{')) {
            const projectDetails = JSON.parse(record.ProjectDetails);
            projectName = Object.values(projectDetails)[0];
          } else {
            projectName = record.ProjectDetails;
          }
        } catch (e) {
          console.error("Error parsing ProjectDetails:", e);
          projectName = 'Unknown Project';
        }
      }
      
      if (projectName) {
        groups[key].projects.add(projectName);
      }
      
      // Check if this is potentially a first request
      try {
        const history = record.History ? JSON.parse(record.History) : {};
        const firstHistoryEntry = Object.values(history)[0];
        const isFirstRequest = firstHistoryEntry && firstHistoryEntry.firstRequest === true;
        
        // If this is a first request and we don't already have one, or if its date is earlier than the existing one
        if (isFirstRequest && (!groups[key].firstRequest || new Date(dateValue) < new Date(groups[key].firstRequest.createdAt || groups[key].firstRequest.DOFReq))) {
          groups[key].firstRequest = record;
        }
      } catch (e) {
        console.error("Error checking for first request:", e);
      }
    });
    
    // Check if we have any groups
    if (Object.keys(groups).length === 0) {
      console.log("No groups were created from finRecordsData");
    } else {
      console.log("Created time groups:", Object.keys(groups));
    }
    
    // Sort groups by date (newest first)
    return Object.fromEntries(
      Object.entries(groups).sort(([keyA], [keyB]) => {
        return new Date(keyB) - new Date(keyA);
      })
    );
  };

  const toggleGroup = (groupId) => {
    setOpenGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const toggleAllGroups = () => {
    setExpandAll(!expandAll);
    const timeGroups = groupRecordsByTimeRange();
    const newOpenGroups = {};
    const newExpandedProjects = {};
    
    Object.keys(timeGroups).forEach(groupId => {
      newOpenGroups[groupId] = !expandAll;
      
      // If we're expanding all, also expand all projects for each time group
      if (!expandAll) {
        const projectGroups = groupRecordsByProject(timeGroups[groupId].records);
        Object.keys(projectGroups).forEach(projectName => {
          const projectKey = `${groupId}-${projectName}`;
          newExpandedProjects[projectKey] = true;
        });
      }
    });
    
    setOpenGroups(newOpenGroups);
    setExpandedProjects(prev => ({
      ...prev,
      ...newExpandedProjects
    }));
  };

  const formatDateRange = (key) => {
    const date = new Date(key);
    
    switch(viewMode) {
      case 'monthly':
        return date.toLocaleString('default', { month: 'long', year: 'numeric' });
      case 'weekly':
        const endDate = new Date(date);
        endDate.setDate(date.getDate() + 6);
        return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      default:
        return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  return (
    <div className="records-container">
      <div
        className="requests-filtersContainer"
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "100%",
          margin: "0 auto 20px",
          alignItems: "center",
          padding: "16px 20px",
          backgroundColor: "#ffffff",
          borderRadius: "10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          borderBottom: "2px solid #fdd34d",
          boxSizing: "border-box",
          flexWrap: "wrap",
          gap: "15px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            flex: 2,
          }}
        >
          <DropDownMUI
            placeholder={"Project"}
            disabeSorting={true}
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            options={["All", ...(Array.isArray(projectNames) ? projectNames : [])]}
            size={"small"}
            custStyles={{ minWidth: "140px" }}
          />
          
          <DropDownMUI
            placeholder={"Category"}
            disabeSorting={true}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            options={["All", ...(Array.isArray(categoryNames) ? categoryNames : [])]}
            size={"small"}
            custStyles={{ minWidth: "130px" }}
          />
          
          <DropDownMUI
            placeholder={"Status"}
            disabeSorting={true}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={["All", "Approved", "Pending", "Declined"]}
            size={"small"}
            custStyles={{ minWidth: "120px" }}
          />
          
          <div>
            <CustomDateRangePicker
              DateRange={DateRangeValue}
              setDateRange={setDateRangeValue}
            />
          </div>
          
          <TimeBubbles viewMode={viewMode} setViewMode={setViewMode} />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <button
            onClick={toggleAllGroups}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: "#f6f6f6",
              border: "1px solid #e0e0e0",
              padding: "6px 14px",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: "500",
              color: "#555",
              transition: "all 0.2s ease",
              position: "relative",
              minWidth: "110px",
              justifyContent: "center",
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                fontSize: "14px",
                transform: expandAll ? "rotate(90deg)" : "rotate(0)",
                display: "inline-block",
                width: "14px",
                height: "14px",
                textAlign: "center",
                lineHeight: "14px",
                transition: "transform 0.3s ease",
              }}
            >
              ▶
            </span>
            {expandAll ? "Collapse All" : "Expand All"}
          </button>

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#555",
                whiteSpace: "nowrap",
                fontWeight: "500",
              }}
            >
              Total Amount:
            </p>
            <p
              style={{
                fontWeight: 700,
                fontSize: "18px",
                margin: 0,
                color: "#333",
                display: "flex",
                alignItems: "center",
                gap: "2px",
              }}
            >
              <span style={{ fontSize: "16px", marginRight: "2px" }}>₹</span>
              {calculateTotalAmount().toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}
            </p>
          </div>
        </div>
      </div>
      
      {/* Light-weight refreshing indicator that doesn't hide existing content */}
      {isRefreshing && !isLoading && (
        <div className="refresh-indicator" style={{
          position: "fixed",
          top: "10px",
          right: "10px",
          background: "rgba(255, 255, 255, 0.85)",
          padding: "8px 12px",
          borderRadius: "4px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          zIndex: 1000
        }}>
          <CircularProgress
            style={{ width: "16px", height: "16px", color: "#fdd34d" }}
          />
          <span style={{ fontSize: "14px" }}>Refreshing data...</span>
        </div>
      )}
      
      <div className="records-content">
        {isLoading ? (
          <div className="loading-container">
            <CircularProgress
              style={{ width: "30px", height: "30px", color: "#fdd34d" }}
            />
          </div>
        ) : finRecordsData && finRecordsData.length > 0 ? (
          (() => {
            const timeGroups = groupRecordsByTimeRange();
            console.log("Time groups for rendering:", Object.keys(timeGroups).length);
            
            if (Object.keys(timeGroups).length === 0) {
              return <h4 className="no-records">No records found with the current filters!</h4>;
            }
            
            return Object.entries(timeGroups).map(([groupId, group]) => (
              <div key={groupId} className="time-group" style={{ 
                marginBottom: "15px", 
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                overflow: "hidden"
              }}>
                <div 
                  className="time-group-header" 
                  onClick={() => toggleGroup(groupId)}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "15px",
                    backgroundColor: "#f9fafc",
                    cursor: "pointer",
                    borderBottom: openGroups[groupId] ? "1px solid #e0e0e0" : "none"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <FaCalendarAlt style={{ marginRight: "10px", color: "#666" }} />
                    <span style={{ fontWeight: "600", fontSize: "16px" }}>{formatDateRange(groupId)}</span>
                    <div style={{ marginLeft: "10px" }}>
                      {openGroups[groupId] ? <FaChevronDown /> : <FaChevronRight />}
                    </div>
                  </div>
                  
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "15px"
                  }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div style={{
                        height: "8px", 
                        width: "8px", 
                        backgroundColor: "#4C9AFF", 
                        borderRadius: "50%", 
                        marginRight: "5px" 
                      }}></div>
                      <span>Total: {group.projects.size} {group.projects.size === 1 ? 'Project' : 'Projects'}</span>
                    </div>
                    
                    {/* Check for first request amount */}
                    {group.firstRequest && (
                      <div style={{
                        padding: "5px 12px",
                        backgroundColor: "#edf7ed",
                        borderRadius: "4px",
                        fontWeight: "600",
                        color: "#1e4620",
                        border: "1px solid #c6e7c6"
                      }}>
                        First Request: ₹{(group.firstRequest.requestItemData?.amount || 0).toLocaleString('en-IN')}
                      </div>
                    )}
                    
                    <div style={{
                      padding: "5px 12px",
                      backgroundColor: "#fffdf5",
                      borderRadius: "4px",
                      fontWeight: "600",
                      color: "#333",
                      border: "1px solid #fdd34d"
                    }}>
                      ₹{group.total.toLocaleString('en-IN')}
                    </div>
                  </div>
                </div>
                
                {openGroups[groupId] && (
                  <div className="project-groups" style={{ padding: "0 15px" }}>
                    {viewMode === "daily" ? (
                      // For daily view, directly show the records without grouping by project
                      <div style={{ padding: "15px 0" }}>
                        {group.records.map((record, index) => {
                          const isFirstRequest = isFirstRequestItem(record);
                          return (
                            <div
                              key={`${groupId}-record-${index}`}
                              style={{
                                position: "relative",
                                ...(isFirstRequest && {
                                  border: "2px solid #fbbf24",
                                  borderRadius: "12px",
                                  backgroundColor: "#fffbeb",
                                  padding: "8px",
                                  margin: "4px 0",
                                  boxShadow: "0 2px 8px rgba(251, 191, 36, 0.15)",
                                })
                              }}
                            >
                              {isFirstRequest && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "-8px",
                                    right: "8px",
                                    backgroundColor: "#fbbf24",
                                    color: "#78350f",
                                    padding: "2px 8px",
                                    borderRadius: "12px",
                                    fontSize: "10px",
                                    fontWeight: "600",
                                    zIndex: 1,
                                  }}
                                >
                                  FIRST REQUEST
                                </div>
                              )}
                              <Req_Rec_Card
                                rIndex={index}
                                setRawData={setRawData}
                                request={record}
                                isRecord={true}
                                setFinRequestsData={setFinRecordsData}
                                className="nested-card"
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      (() => {
                        // Get all projects for this time period
                        const projectGroups = groupRecordsByProject(group.records);
                        
                        if (Object.keys(projectGroups).length === 0) {
                          return (
                            <div style={{ padding: "15px", textAlign: "center", color: "#666" }}>
                              No projects found for this time period.
                            </div>
                          );
                        }
                        
                        // Map each project to its component
                        return Object.entries(projectGroups).map(([projectName, projectData]) => {
                          
                          if (!projectData) {
                            console.log(`Project ${projectName} has no data`);
                            return null;
                          }
                          
                          const { records: projectRecords, total: projectTotal, firstRequest: projectFirstRequest } = projectData;
                          const projectKey = `${groupId}-${projectName}`;
                          const isExpanded = Boolean(expandedProjects[projectKey]);
                          
                          
                          return (
                            <div key={projectKey} className="project-group" style={{ 
                              margin: "15px 0",
                              border: "1px solid #e0e0e0",
                              borderRadius: "8px",
                              overflow: "hidden"
                            }}>
                              <div 
                                className="project-group-header" 
                                onClick={() => toggleProject(projectKey)}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "12px 15px",
                                  backgroundColor: "#f7f8fa",
                                  cursor: "pointer",
                                  borderBottom: isExpanded ? "1px solid #e0e0e0" : "none"
                                }}
                              >
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  <span style={{ fontWeight: "500", marginRight: "10px" }}>{projectName}</span>
                                  <div>
                                    {isExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                                  </div>
                                </div>
                                
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                  {projectFirstRequest && (
                                    <div style={{
                                      padding: "4px 10px",
                                      backgroundColor: "#edf7ed",
                                      borderRadius: "4px",
                                      fontSize: "0.875rem",
                                      fontWeight: "500",
                                      color: "#1e4620",
                                      border: "1px solid #c6e7c6"
                                    }}>
                                      First: ₹{(projectFirstRequest.requestItemData?.amount || 0).toLocaleString('en-IN')}
                                    </div>
                                  )}
                                  
                                  <div style={{
                                    padding: "4px 10px",
                                    backgroundColor: "#f0f1f3",
                                    borderRadius: "4px",
                                    fontSize: "0.875rem",
                                    fontWeight: "500"
                                  }}>
                                    ₹{projectTotal.toLocaleString('en-IN')}
                                  </div>
                                </div>
                              </div>
                              
                              {expandedProjects[projectKey] && (
                                <div className="project-records" style={{ padding: "10px" }}>
                                  {(() => {
                                    console.log(`Project ${projectName} records being rendered:`, {
                                      recordsExists: Boolean(projectRecords),
                                      recordsIsArray: Array.isArray(projectRecords),
                                      recordsLength: projectRecords?.length || 0,
                                      projectKey
                                    });
                                    
                                    // Handle non-array case
                                    if (!Array.isArray(projectRecords)) {
                                      console.error("Project records is not an array:", projectRecords);
                                      return (
                                        <div style={{ padding: "15px", textAlign: "center", color: "#666" }}>
                                          Error: Records data is not in the expected format.
                                        </div>
                                      );
                                    }
                                    
                                    // Handle empty array case
                                    if (projectRecords.length === 0) {
                                      return (
                                        <div style={{ padding: "15px", textAlign: "center", color: "#666" }}>
                                          No records found for this project.
                                        </div>
                                      );
                                    }
                                    
                                    // Actually render the records
                                    return projectRecords.map((record, index) => {
                                      // Skip invalid records
                                      if (!record) {
                                        console.warn(`Record at index ${index} is null or undefined`);
                                        return null;
                                      }
                                      
                                      // Double-check record has minimum required fields
                                      if (!record.uuid || (!record.requestItemData && !record.History)) {
                                        console.warn(`Record at index ${index} is missing required fields:`, record);
                                        return (
                                          <div key={`error-${index}`} style={{ 
                                            padding: "10px",
                                            margin: "5px 0",
                                            backgroundColor: "#fff4f4",
                                            border: "1px solid #ffdddd",
                                            borderRadius: "4px"
                                          }}>
                                            Record data incomplete (ID: {record.uuid || 'unknown'})
                                          </div>
                                        );
                                      }
                                      
                                      // Render the record card
                                      const isFirstRequest = isFirstRequestItem(record);
                                      return (
                                        <div
                                          key={`${projectKey}-record-${index}`}
                                          style={{
                                            position: "relative",
                                            ...(isFirstRequest && {
                                              border: "2px solid #fbbf24",
                                              borderRadius: "12px",
                                              backgroundColor: "#fffbeb",
                                              padding: "8px",
                                              margin: "4px 0",
                                              boxShadow: "0 2px 8px rgba(251, 191, 36, 0.15)",
                                            })
                                          }}
                                        >
                                          {isFirstRequest && (
                                            <div
                                              style={{
                                                position: "absolute",
                                                top: "-8px",
                                                right: "8px",
                                                backgroundColor: "#fbbf24",
                                                color: "#78350f",
                                                padding: "2px 8px",
                                                borderRadius: "12px",
                                                fontSize: "10px",
                                                fontWeight: "600",
                                                zIndex: 1,
                                              }}
                                            >
                                              FIRST REQUEST
                                            </div>
                                          )}
                                          <Req_Rec_Card
                                            rIndex={index}
                                            setRawData={setRawData}
                                            request={record}
                                            isRecord={true}
                                            setFinRequestsData={setFinRecordsData}
                                            className="nested-card"
                                          />
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()
                    )}
                  </div>
                )}
              </div>
            ));
          })()
        ) : (
          <h4 className="no-records">Nothing to show here! Try adjusting your filters.</h4>
        )}
      </div>
    </div>
  );
};

export default Records_Fin;
